import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedCastPicturePending, DirectedCastRole } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { castMessages as messages } from '../messages/castMessages';
import { CastRoles } from './CastRoles';
import { failedPictureMessage } from './failedPictureRules';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FailedCastCardProps {
  picture: DirectedCastPicturePending;
  readOnly: boolean;
  /** False when the video has no AI pictures left or the cast is full. */
  canMake: boolean;
  /** True while a request of this card runs. */
  isBusy: boolean;
  /** Asks the AI once more with the same words and the role on the card. */
  onTryAgain: (role: DirectedCastRole) => void;
  /** Opens the picture maker with the last words, so the artist can change them. */
  onChangeWords: (role: DirectedCastRole) => void;
  onRemove: () => void;
}

/**
 * A cast picture that the AI did not make (owner report 2026-10-20, the "stone walker" card): it says
 * why when the server knows it (`failReason`: refused by the image model, a technical error, the AI
 * is busy), and it offers a way forward: "Try again" with the same words, "Change the words" (the
 * picture maker with the last words), the × of a ready card, and the role switch. The styles of a
 * ready card (design b3), with the error tone of the design.
 */
const FailedCastCardComponent: React.FC<FailedCastCardProps> = ({
  picture,
  readOnly,
  canMake,
  isBusy,
  onTryAgain,
  onChangeWords,
  onRemove,
}) => {
  const intl = useIntl();
  const [role, setRole] = useState<DirectedCastRole>(picture.role);
  useEffect(() => setRole(picture.role), [picture.role]);
  const name = picture.label === '' ? intl.formatMessage(messages.unnamed) : picture.label;
  const hasWords = (picture.description ?? '').trim() !== '';
  // A refusal fails again with the same words: "Change the words" is the main action then.
  const refused = picture.failReason === 'refused';

  return (
    <div
      className={clsx(styles.castCard, styles.castCardFailed)}
      role="status"
      data-testid="cast-pending"
      data-status="failed"
    >
      <div className={clsx(styles.picture, styles.pictureWorking)}>
        {!readOnly && (
          <button
            type="button"
            className={styles.remove}
            aria-label={intl.formatMessage(messages.remove, { label: name })}
            disabled={isBusy}
            onClick={onRemove}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>
      <div className={styles.castBody}>
        <div className={styles.labelText}>{name}</div>
        <CastRoles value={role} label={name} disabled={readOnly || isBusy} onChange={setRole} />
        <span className={styles.error}>{intl.formatMessage(failedPictureMessage(picture.failReason))}</span>
        {!readOnly && (
          <div className={styles.row}>
            <SMLButton
              variant={refused ? 'default' : 'primary'}
              disabled={!canMake || isBusy || !hasWords}
              onClick={() => onTryAgain(role)}
            >
              {intl.formatMessage(messages.tryAgain)}
            </SMLButton>
            <SMLButton
              variant={refused ? 'primary' : 'default'}
              disabled={!canMake || isBusy}
              onClick={() => onChangeWords(role)}
            >
              {intl.formatMessage(messages.changeWords)}
            </SMLButton>
          </div>
        )}
      </div>
    </div>
  );
};

export const FailedCastCard = TypedMemo(FailedCastCardComponent);
